import React, { useState } from "react";
import {
  FiHash,
  FiCheckCircle,
  FiMail,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import useApi from "../../../CustomHooks/useApi";

const BasicDetailsSection = ({ onSave, onEdit }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  const [isReEnterPan, setIsReEnterPan] = useState(false); // 🔥 NEW

  const [form, setForm] = useState({
    panNumber: "",
    dob: "",
    fullName: "",
    fatherName: "",
    gender: "",
    email: "",
    mobileNumber: "",
  });

  const [panVerified, setPanVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errors, setErrors] = useState({});
  const [panError, setPanError] = useState("");

  const { callApi: panverify } = useApi(
    "/staff/onboard/verify-pan",
    "POST"
  );

  /* ---------------- VERIFY PAN (USED FOR VERIFY & RE-VERIFY) ---------------- */

  const verifyPan = async () => {
    if (!form.panNumber || form.panNumber.length !== 10) {
      setPanError("Enter valid 10-character PAN number");
      return;
    }

    try {
      setIsVerifying(true);
      setPanError("");

      const res = await panverify({
        panNumber: form.panNumber,
      });

      if (res?.status === "success") {
        setPanVerified(true);
        setIsReEnterPan(false);

        setForm((prev) => ({
          ...prev,
          fullName: res.fullName || "",
        }));
      } else {
        setPanError("PAN verification failed");
      }
    } catch (err) {
      setPanError("Something went wrong");
    } finally {
      setIsVerifying(false);
    }
  };

  /* ---------------- VALIDATION ---------------- */

  const validate = () => {
    const e = {};

    if (!form.mobileNumber || form.mobileNumber.length !== 10)
      e.mobileNumber = "Enter valid mobile number";
    if (!form.email) e.email = "Email is required";
    if (!form.dob) e.dob = "Date of birth is required";
    if (!form.fullName) e.fullName = "Full name is required";
    if (!form.fatherName) e.fatherName = "Father name is required";
    if (!form.gender) e.gender = "Select gender";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- SAVE ---------------- */

  const handleSave = () => {
    if (!panVerified) return;
    if (!validate()) return;

    setIsSaved(true);
    onSave(form);
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = () => {
    setIsSaved(false);
    onEdit();
  };

  /* ---------------- RE-ENTER PAN ---------------- */

  const handleReEnterPan = () => {
    setIsReEnterPan(true);
    setPanVerified(false);
    setErrors({});
    setPanError("");
    onEdit();

    setForm((prev) => ({
      ...prev,
      panNumber: "",
      dob: "",
      fullName: "",
      fatherName: "",
      gender: "",
      email: "",
      mobileNumber: "",
    }));
  };

  const disabled = isSaved;

  /* ---------------- UI ---------------- */

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* HEADER */}
      <div
        className="flex cursor-pointer items-center justify-between border-b px-6 py-4"
        onClick={() => setIsOpen((p) => !p)}
      >
        <h3 className="text-sm font-semibold text-gray-800">
          Basic Information
        </h3>

        {isOpen ? (
          <FiChevronUp className="text-gray-600" />
        ) : (
          <FiChevronDown className="text-gray-600" />
        )}
      </div>

      {isOpen && (
        <div className="p-6">
          {/* PAN */}
          <div className="mb-6">
            <label className="text-xs font-medium text-gray-600">
              PAN Number *
            </label>

            <div className="relative mt-1">
              <FiHash className="absolute left-3 top-3 text-gray-400" />
              <input
                disabled={panVerified && !isReEnterPan}
                className="w-full rounded-md border px-9 py-2 text-sm bg-gray-50"
                value={form.panNumber}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    panNumber: e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, ""),
                  }))
                }
                placeholder="ABCDE1234F"
              />

              {panVerified && (
                <FiCheckCircle className="absolute right-3 top-3 text-green-600" />
              )}
            </div>

            {panError && (
              <p className="mt-1 text-xs text-red-500">{panError}</p>
            )}

            {/* FIRST VERIFY */}
            {!panVerified && !isReEnterPan && (
              <button
                onClick={verifyPan}
                disabled={isVerifying}
                className="mt-3 rounded-full bg-[#0A2A4F] px-6 py-2 text-sm text-white"
              >
                {isVerifying ? "Verifying PAN..." : "Verify PAN"}
              </button>
            )}

            {/* RE-ENTER PAN */}
            {isSaved && !isReEnterPan && (
              <button
                onClick={handleReEnterPan}
                className="mt-3 ml-3 rounded-full bg-gray-500 px-6 py-2 text-sm text-white"
              >
                Re-Enter PAN
              </button>
            )}

            {/* RE-VERIFY PAN */}
            {isReEnterPan && (
              <button
                onClick={verifyPan}
                disabled={isVerifying}
                className="mt-3 rounded-full bg-[#0A2A4F] px-6 py-2 text-sm text-white"
              >
                {isVerifying ? "Re-Verifying PAN..." : "Re-Verify PAN"}
              </button>
            )}
          </div>

          {/* OTHER INPUTS */}
          {panVerified && !isReEnterPan && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Mobile */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Mobile Number *
                  </label>
                  <input
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.mobileNumber}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        mobileNumber: e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10),
                      }))
                    }
                  />
                  {errors.mobileNumber && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Email *
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-3 text-gray-400" />
                    <input
                      disabled={disabled}
                      className="w-full rounded-md border px-9 py-2 text-sm"
                      value={form.email}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          email: e.target.value,
                        }))
                      }
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* DOB */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.dob}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, dob: e.target.value }))
                    }
                  />
                  {errors.dob && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.dob}
                    </p>
                  )}
                </div>

                {/* Full Name */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Full Name *
                  </label>
                  <input
                    disabled={disabled || panVerified}
                    className="w-full rounded-md border px-3 py-2 text-sm bg-gray-50"
                    value={form.fullName}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        fullName: e.target.value,
                      }))
                    }
                  />
                  {errors.fullName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Father Name */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Father Name *
                  </label>
                  <input
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.fatherName}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        fatherName: e.target.value,
                      }))
                    }
                  />
                  {errors.fatherName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.fatherName}
                    </p>
                  )}
                </div>

                {/* Gender */}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    Gender *
                  </label>
                  <select
                    disabled={disabled}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={form.gender}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        gender: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.gender}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                {!isSaved ? (
                  <button
                    onClick={handleSave}
                    className="rounded-full bg-green-600 px-8 py-2 text-sm text-white"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="rounded-full bg-gray-500 px-8 py-2 text-sm text-white"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicDetailsSection;


